import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Divider,
  useMediaQuery,
  Drawer,
  useTheme
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FolderIcon from '@mui/icons-material/Folder';
import CampaignIcon from '@mui/icons-material/Campaign';
import PublicIcon from '@mui/icons-material/Public';
import BoltIcon from '@mui/icons-material/Bolt';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import GroupIcon from '@mui/icons-material/Group';
import StorageIcon from '@mui/icons-material/Storage';
import TableChartIcon from '@mui/icons-material/TableChart';
import InventoryIcon from '@mui/icons-material/Inventory';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import BuildIcon from '@mui/icons-material/Build';
import LanguageIcon from '@mui/icons-material/Language';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SettingsIcon from '@mui/icons-material/Settings';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HelpIcon from '@mui/icons-material/Help';
import LogoutIcon from '@mui/icons-material/Logout';
import SchoolIcon from '@mui/icons-material/School';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';

const SIDEBAR_WIDTH_EXPANDED = 220;
const SIDEBAR_WIDTH_COLLAPSED = 64;

const SIDEBAR_SECTIONS = [
  {
    label: 'CORE',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: DashboardIcon, path: '/' },
      { id: 'agent-ops', label: 'Agent Ops', icon: ManageSearchIcon, path: '/agent-ops' },
      { id: 'virtual-coffee', label: 'Virtual Coffee', icon: LocalCafeIcon, path: '/virtual-coffee' },
      { id: 'oasis', label: 'Oasis', icon: EmailIcon, path: '/oasis' },
      { id: 'hub', label: 'Hub', icon: PublicIcon, path: '/hub' },
    ]
  },
  {
    label: 'BUILD',
    items: [
      { id: 'domino', label: 'Campaigns', icon: CampaignIcon, path: '/domino' },
      { id: 'gks', label: 'Global Knowledge', icon: PublicIcon, path: '/gks' },
      { id: 'signal', label: 'Signal or Noise', icon: BoltIcon, path: '/signal' },
    ]
  },
  {
    label: 'DESKTOP',
    items: [
      { id: 'mirror', label: 'Mirror Hub', icon: ContentCopyIcon, path: '/mirror' },
      { id: 'dscan', label: 'D-Scanner', icon: FindInPageIcon, path: '/dscan' },
    ]
  },
  {
    label: 'CONTENT',
    items: [
      { id: 'aph-wp', label: 'APH', icon: PublicIcon, path: '/wp/aph' },
      { id: 'lec-wp', label: 'LEC', icon: PublicIcon, path: '/wp/lec' },
      { id: 'ini-wp', label: 'INI', icon: PublicIcon, path: '/wp/ini' },
      { id: 'tonisha-wp', label: 'Tonisha', icon: PublicIcon, path: '/wp/tonisha' },
      { id: 'webcreat-wp', label: 'WebCreators', icon: PublicIcon, path: '/wp/webcreat' },
      { id: 'aiex', label: 'AI Excellence Hub', icon: SchoolIcon, path: '/aiex' },
    ]
  },
  {
    label: 'AUTOMATION',
    items: [
      { id: 'n8n', label: 'n8n', icon: AccountTreeIcon, path: '/n8n' },
      { id: 'browser', label: 'Browser', icon: LanguageIcon, path: '/browser' },
    ]
  }
];

const BOTTOM_ITEMS = [
  { id: 'settings', label: 'Settings', icon: SettingsIcon, path: '/settings' },
];

const Sidebar = ({ expanded, onToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const width = expanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/projects' && path === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNav = (path) => {
    navigate(path);
    if (isMobile) onToggle();
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
  };

  const renderItem = (item) => {
    const active = isActive(item.path);
    const Icon = item.icon;
    return (
      <Tooltip
        key={item.id}
        title={expanded ? '' : item.label}
        placement="right"
        arrow
      >
        <Box
          onClick={() => handleNav(item.path)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: expanded ? 2 : 0,
            py: 0.75,
            mx: expanded ? 1 : 0.75,
            borderRadius: 1,
            cursor: 'pointer',
            justifyContent: expanded ? 'flex-start' : 'center',
            backgroundColor: active ? 'rgba(255,255,255,0.12)' : 'transparent',
            color: active ? '#fff' : 'rgba(255,255,255,0.6)',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: active ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.06)',
              color: '#fff',
            }
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
          {expanded && (
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                fontWeight: active ? 600 : 400,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {item.label}
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  };

  const sidebarContent = (
    <Box
      sx={{
        width,
        height: '100vh',
        backgroundColor: '#002B45',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
        position: 'fixed',
        left: 0,
        top: 0,
        flexShrink: 0,
        zIndex: 1100,
      }}
    >
      {/* Top: Toggle + Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: expanded ? 2 : 0, py: 1.5, justifyContent: expanded ? 'space-between' : 'center' }}>
        {expanded && (
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 500,
              color: '#fff',
              fontSize: '1.1rem',
              letterSpacing: '-0.3px',
              whiteSpace: 'nowrap',
            }}
          >
            Data Insights
          </Typography>
        )}
        <IconButton onClick={onToggle} size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {expanded ? <MenuOpenIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 1 }} />

      {/* Scrollable sections */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', '&::-webkit-scrollbar': { width: 0 } }}>
        {SIDEBAR_SECTIONS.map((section, idx) => (
          <Box key={section.label} sx={{ mb: 1.5 }}>
            {expanded && (
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.6rem',
                  color: 'rgba(255,255,255,0.35)',
                  px: 2,
                  display: 'block',
                  letterSpacing: '0.1em',
                  mb: 0.5,
                }}
              >
                {section.label}
              </Typography>
            )}
            {!expanded && idx > 0 && (
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 1, mb: 0.5 }} />
            )}
            {section.items.map(renderItem)}
          </Box>
        ))}
      </Box>

      {/* Bottom: Settings, Upload, Help, Logout */}
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      <Box sx={{ pb: 1, pt: 0.5 }}>
        {BOTTOM_ITEMS.map(renderItem)}
        <Tooltip title={expanded ? '' : 'Sign out'} placement="right" arrow>
          <Box
            onClick={handleLogout}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: expanded ? 2 : 0,
              py: 0.75,
              mx: expanded ? 1 : 0.75,
              borderRadius: 1,
              cursor: 'pointer',
              justifyContent: expanded ? 'flex-start' : 'center',
              color: 'rgba(255,255,255,0.4)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.06)',
                color: 'rgba(255,200,200,0.8)',
              }
            }}
          >
            <LogoutIcon sx={{ fontSize: 20 }} />
            {expanded && (
              <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                Sign out
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  // Mobile: drawer overlay
  if (isMobile) {
    return (
      <>
        {/* Floating toggle for mobile */}
        <IconButton
          onClick={onToggle}
          sx={{
            position: 'fixed',
            top: 8,
            left: 8,
            zIndex: 1200,
            backgroundColor: '#002B45',
            color: '#fff',
            '&:hover': { backgroundColor: '#003a5c' },
            boxShadow: 2,
          }}
        >
          <MenuIcon />
        </IconButton>
        <Drawer
          open={expanded}
          onClose={onToggle}
          sx={{ '& .MuiDrawer-paper': { border: 'none' } }}
        >
          {React.cloneElement(sidebarContent, {})}
        </Drawer>
      </>
    );
  }

  // Desktop: fixed sidebar
  return sidebarContent;
};

export { SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_COLLAPSED };
export default Sidebar;
